import { Property } from '../types';
import { api } from './api';

// Helper to normalize API response to frontend Property type
const normalizeProperty = (apiProperty: Record<string, unknown>): Property => {
  return {
    id: apiProperty.id as string,
    name: apiProperty.title as string || apiProperty.name as string,
    title: apiProperty.title as string || apiProperty.name as string || '',
    description: apiProperty.description as string || '',
    address: apiProperty.address as string || '',
    city: apiProperty.city as string || '',
    state: apiProperty.state as string || '',
    zip_code: (apiProperty.zipCode as string) || (apiProperty.zip_code as string) || '',
    property_type: (apiProperty.propertyType as Property['property_type']) || (apiProperty.property_type as Property['property_type']) || 'residential',
    total_value: (apiProperty.totalValue as number) || (apiProperty.total_value as number) || 0,
    total_shares: (apiProperty.totalShares as number) || (apiProperty.total_shares as number) || 1000,
    available_shares: (apiProperty.availableShares as number) || (apiProperty.available_shares as number) || 0,
    price_per_share: (apiProperty.pricePerShare as number) || (apiProperty.price_per_share as number) || 100,
    annual_yield: (apiProperty.annualYield as number) || (apiProperty.annual_yield as number) || 0.08,
    status: (apiProperty.status as Property['status']) || 'active',
    images: apiProperty.images as string[] || [],
    documents: apiProperty.documents as string[] || [],
    verification_status: (apiProperty.verificationStatus as Property['verification_status']) || (apiProperty.verification_status as Property['verification_status']) || 'pending',
    created_at: (apiProperty.createdAt as string) || (apiProperty.created_at as string) || new Date().toISOString(),
    updated_at: (apiProperty.updatedAt as string) || (apiProperty.updated_at as string) || new Date().toISOString(),
    shares_sold: (apiProperty.sharesSold as number) || (apiProperty.shares_sold as number) || 0,
    completion_percentage: (apiProperty.completionPercentage as number) || (apiProperty.completion_percentage as number) || 0,
    market_cap: (apiProperty.marketCap as number) || (apiProperty.market_cap as number) || 0,
    stakersCount: (apiProperty.stakersCount as number) || 0,
    propertyData: (apiProperty.propertyData as Record<string, any>) || null,
    tags: (apiProperty.tags as string[]) || [],
  };
};

export const propertyService = {
  // Get all properties
  async getProperties(): Promise<Property[]> {
    try {
      console.log('[propertyService] Calling API /properties...');
      const response = await api.get('/properties');
      console.log('[propertyService] Raw response:', response);
      // api.get already returns axios response.data, so response IS the data object
      // Cast through unknown to handle the actual shape from backend
      const responseData = response as unknown as Record<string, unknown>;
      
      // Handle paginated response: { properties: [...], pagination }
      if (responseData && 'properties' in responseData && Array.isArray(responseData.properties)) {
        console.log('[propertyService] Found properties array with', responseData.properties.length, 'items');
        const normalized = (responseData.properties as Record<string, unknown>[]).map(normalizeProperty);
        console.log('[propertyService] Normalized properties:', normalized.length);
        return normalized;
      }
      // Handle { success, data: [...] } format
      if (responseData && 'data' in responseData && Array.isArray(responseData.data)) {
        console.log('[propertyService] Found data array format');
        return (responseData.data as Record<string, unknown>[]).map(normalizeProperty);
      }
      // Handle direct array response
      if (Array.isArray(responseData)) {
        console.log('[propertyService] Found direct array format');
        return (responseData as Record<string, unknown>[]).map(normalizeProperty);
      }
      console.log('[propertyService] No matching format, returning empty array. Keys:', Object.keys(responseData || {}));
      return [];
    } catch (error) {
      console.error('[propertyService] Error fetching properties:', error);
      throw new Error('Failed to fetch properties');
    }
  },

  // Get property by ID
  async getProperty(id: string): Promise<Property> {
    try {
      const response = await api.get(`/properties/${id}`);
      return response.data as Property;
    } catch (error) {
      console.error('Error fetching property:', error);
      throw new Error('Failed to fetch property');
    }
  },

  // Get properties by type
  async getPropertiesByType(type: string): Promise<Property[]> {
    try {
      const response = await api.get(`/properties?type=${type}`);
      return response.data as Property[];
    } catch (error) {
      console.error('Error fetching properties by type:', error);
      throw new Error('Failed to fetch properties by type');
    }
  },

  // Search properties
  async searchProperties(query: string): Promise<Property[]> {
    try {
      const response = await api.get(`/properties/search?q=${encodeURIComponent(query)}`);
      return response.data as Property[];
    } catch (error) {
      console.error('Error searching properties:', error);
      throw new Error('Failed to search properties');
    }
  },

  // Get property investment stats
  async getPropertyStats(id: string) {
    try {
      const response = await api.get(`/properties/${id}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property stats:', error);
      throw new Error('Failed to fetch property stats');
    }
  },

  // Get property documents
  async getPropertyDocuments(id: string) {
    try {
      const response = await api.get(`/properties/${id}/documents`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property documents:', error);
      throw new Error('Failed to fetch property documents');
    }
  },

  // Investment actions
  async investInProperty(propertyId: string, amount: number, shares: number) {
    try {
      const response = await api.post(`/properties/${propertyId}/invest`, {
        amount,
        shares
      });
      return response.data;
    } catch (error) {
      console.error('Error investing in property:', error);
      throw new Error('Failed to invest in property');
    }
  },

  // Get available investment amounts for a property
  async getInvestmentOptions(propertyId: string) {
    try {
      const response = await api.get(`/properties/${propertyId}/investment-options`);
      return response.data;
    } catch (error) {
      console.error('Error fetching investment options:', error);
      throw new Error('Failed to fetch investment options');
    }
  },

  // Admin functions
  async createProperty(propertyData: Partial<Property>) {
    try {
      const response = await api.post('/admin/properties', propertyData);
      return response.data;
    } catch (error) {
      console.error('Error creating property:', error);
      throw new Error('Failed to create property');
    }
  },

  async updateProperty(id: string, propertyData: Partial<Property>) {
    try {
      const response = await api.put(`/admin/properties/${id}`, propertyData);
      return response.data;
    } catch (error) {
      console.error('Error updating property:', error);
      throw new Error('Failed to update property');
    }
  },

  async deleteProperty(id: string) {
    try {
      const response = await api.delete(`/admin/properties/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting property:', error);
      throw new Error('Failed to delete property');
    }
  },

  // Upload properties via CSV
  async uploadPropertiesCSV(file: File, batchName?: string) {
    try {
      const formData = new FormData();
      formData.append('csv', file);
      if (batchName) {
        formData.append('batchName', batchName);
      }

      const response = await api.post('/admin/properties/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading properties CSV:', error);
      throw new Error('Failed to upload properties CSV');
    }
  },

  // Get upload batch status
  async getUploadBatchStatus(batchId: string) {
    try {
      const response = await api.get(`/admin/properties/upload-batch/${batchId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching upload batch status:', error);
      throw new Error('Failed to fetch upload batch status');
    }
  }
};
